import { z } from "zod";

import type { TenantListItem } from "@upclaim/sdk/src/data-contracts";

export type AmadeusLinkType = NonNullable<TenantListItem["amadeusLinkType"]>;

/**
 * Agencies whose PNR remarks carry the enterprise identifier. Without it the reservations
 * never attach to the company, so the field is required for them (and hidden for everyone else).
 */
export const amadeusLinkLabels: Record<
  AmadeusLinkType,
  { label: string; placeholder: string; help: string; pastedRemarkError: string }
> = {
  GESTOUR: {
    label: "Numéro Gestour",
    placeholder: "Ex: 0153122",
    help: "Remarque PNR « RM *0106*0153122 » → saisissez uniquement 0153122",
    pastedRemarkError: "Saisissez uniquement le numéro (ex: 0153122), sans la remarque « RM *0106* »",
  },
  RM_SOCIETE: {
    label: "RM Société",
    placeholder: "Ex: UGAP FV",
    help: "Remarque PNR « RM *SOCIETE : UGAP FV » → saisissez uniquement UGAP FV",
    pastedRemarkError:
      "Saisissez uniquement le nom (ex: UGAP FV), sans le préfixe « RM *SOCIETE : » — sinon aucun PNR ne sera rattaché",
  },
  IGA: {
    label: "N° IGA",
    placeholder: "Ex: 402116",
    help: "Remarque PNR « RM* CLI/402116 » → saisissez 402116 (la lettre de la référence IGA C402116 est facultative)",
    pastedRemarkError: "Saisissez uniquement le numéro (ex: 402116), sans la remarque « RM* CLI/ »",
  },
};

/**
 * Column the agency's PNR parser fills — mirrors the API `AmadeusLinkConfig.field`:
 * Globeo matches on the company name, the others on a number.
 */
export function getAmadeusLinkField(type: AmadeusLinkType): "thirdId" | "rawGdsName" {
  return type === "RM_SOCIETE" ? "rawGdsName" : "thirdId";
}

/** The whole PNR remark pasted instead of the value it carries — an exact-match killer. */
const PASTED_REMARK_REGEX = /^RM[\s*]|SOCI[EÉ]T[EÉ]\s*:|^CLI\s*[/:]/i;

const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Luhn checksum, shared by SIREN (9 digits) and SIRET (14). */
function isValidLuhn(digits: string): boolean {
  let sum = 0;
  for (let index = 0; index < digits.length; index++) {
    let digit = Number.parseInt(digits[index], 10);
    if ((digits.length - index) % 2 === 0) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

/**
 * Splits whatever the admin typed into the API fields. Mirrors the server-side
 * `extractSirenFromIdentifier`: a French VAT number carries its SIREN, a SIRET starts with it.
 * Returns null when the value cannot be a legal identifier at all.
 */
export function parseLegalIdentifier(raw: string): { siren?: string; siret?: string; euVAT?: string } | null {
  const cleaned = raw.replace(/[\s.-]/g, "").toUpperCase();

  if (/^FR[0-9A-Z]{2}[0-9]{9}$/.test(cleaned)) {
    const siren = cleaned.slice(4);
    return isValidLuhn(siren) ? { siren, euVAT: cleaned } : null;
  }
  if (/^[A-Z]{2}[0-9A-Z]{2,13}$/.test(cleaned)) {
    return { euVAT: cleaned };
  }
  if (/^[0-9]{14}$/.test(cleaned)) {
    return isValidLuhn(cleaned) ? { siren: cleaned.slice(0, 9), siret: cleaned } : null;
  }
  if (/^[0-9]{9}$/.test(cleaned)) {
    return isValidLuhn(cleaned) ? { siren: cleaned } : null;
  }
  return null;
}

/**
 * One flat schema for both creation modes — the context the rules depend on
 * (`amadeusLinkType`, `parentCompanyId`) lives in the form values, so the resolver
 * stays static while the rules follow the selected tenant and parent company.
 */
export const createCompanyFormSchema = z
  .object({
    /** Upclaim only — the tenant whose travel agency owns the company. */
    tenantId: z.string().nullable(),
    amadeusLinkType: z.enum(["GESTOUR", "RM_SOCIETE", "IGA"]).nullable(),
    /** Set → we create a sub-account of that company instead of a standalone one. */
    parentCompanyId: z.string().nullable(),
    displayName: z.string().trim(),
    /** SIREN, SIRET or VAT number, as typed. */
    identifier: z.string().trim(),
    mainContactEmail: z.string().trim(),
    accountManagerId: z.string().nullable(),
    teamId: z.string().nullable(),
    domain: z.string().trim(),
    additionalDomains: z.array(z.string().trim()),
    bluebizIdentifier: z.string().trim(),
    amadeusLink: z.string().trim(),
  })
  .superRefine((values, ctx) => {
    const isSubAccount = values.parentCompanyId !== null;

    if (!values.displayName) {
      ctx.addIssue({
        code: "custom",
        path: ["displayName"],
        message: isSubAccount ? "Le nom GDS du sous-compte est requis" : "Le nom de la société est requis",
      });
    } else if (
      isSubAccount &&
      values.amadeusLinkType === "RM_SOCIETE" &&
      PASTED_REMARK_REGEX.test(values.displayName)
    ) {
      // For Globeo the sub-account name IS the remark value, same paste trap.
      ctx.addIssue({
        code: "custom",
        path: ["displayName"],
        message: amadeusLinkLabels.RM_SOCIETE.pastedRemarkError,
      });
    }

    if (!isSubAccount) {
      if (!values.identifier) {
        ctx.addIssue({ code: "custom", path: ["identifier"], message: "SIREN, SIRET ou TVA requis" });
      } else if (!parseLegalIdentifier(values.identifier)) {
        ctx.addIssue({
          code: "custom",
          path: ["identifier"],
          message: "Numéro invalide (SIREN 9 chiffres, SIRET 14 chiffres ou n° de TVA)",
        });
      }

      if (!values.domain) {
        ctx.addIssue({ code: "custom", path: ["domain"], message: "Le nom de domaine est requis" });
      } else if (!DOMAIN_REGEX.test(values.domain)) {
        ctx.addIssue({ code: "custom", path: ["domain"], message: "Nom de domaine invalide (ex: acme.fr)" });
      }

      if (values.mainContactEmail && !EMAIL_REGEX.test(values.mainContactEmail)) {
        ctx.addIssue({ code: "custom", path: ["mainContactEmail"], message: "Adresse email invalide" });
      }
    }

    // For Globeo the GDS name IS the link, so a sub-account needs nothing more.
    const linkType = values.amadeusLinkType;
    const needsAmadeusLink =
      linkType !== null && (!isSubAccount || getAmadeusLinkField(linkType) === "thirdId");
    if (needsAmadeusLink && !values.amadeusLink) {
      ctx.addIssue({
        code: "custom",
        path: ["amadeusLink"],
        message: `${amadeusLinkLabels[linkType].label} obligatoire pour cette agence`,
      });
    } else if (linkType && values.amadeusLink && PASTED_REMARK_REGEX.test(values.amadeusLink)) {
      ctx.addIssue({
        code: "custom",
        path: ["amadeusLink"],
        message: amadeusLinkLabels[linkType].pastedRemarkError,
      });
    }
  });

export type CreateCompanyFormValues = z.infer<typeof createCompanyFormSchema>;

export const createCompanyDefaultValues: CreateCompanyFormValues = {
  tenantId: null,
  amadeusLinkType: null,
  parentCompanyId: null,
  displayName: "",
  identifier: "",
  mainContactEmail: "",
  accountManagerId: null,
  teamId: null,
  domain: "",
  additionalDomains: [],
  bluebizIdentifier: "",
  amadeusLink: "",
};

/** Fields validated when leaving each step. */
export const CREATE_COMPANY_STEP_FIELDS = [
  ["displayName", "identifier"],
  ["domain", "amadeusLink"],
  ["mainContactEmail"],
] as const;

/**
 * Placeholder of the dedicated inbox: agencies route a company's notifications to an
 * address on their own domain, e.g. `radiofrance@havasvoyages.fr`.
 */
export function suggestMainContactEmail(displayName: string, agencyDomain: string | null): string {
  // NFD then drop the combining marks, so "Société" reads "societe" instead of "socit".
  const localPart =
    displayName
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "") || "nomentreprise";

  return `Ex: ${localPart}@${agencyDomain ?? "agence.fr"}`;
}
